import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Divider, Alert,
  CircularProgress, IconButton, InputAdornment, Stack, Menu, MenuItem
} from '@mui/material';
import {
  School as SchoolIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Language as LanguageIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useTeacherPortalLanguage } from './contexts/TeacherPortalLanguageContext';

const GoogleSvgIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
  </svg>
);

const MicrosoftSvgIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

interface Props {
  onLogin: (login: string, pass: string) => Promise<void>;
  loading: boolean;
  error?: string | null;
}

export const TeacherPortalLoginPage: React.FC<Props> = ({ onLogin, loading, error }) => {
  const { t, language, setLanguage } = useTeacherPortalLanguage();
  const [loginInput, setLoginInput] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loginInput.trim() && password) {
      onLogin(loginInput.trim(), password);
    }
  };

  const handleSsoClick = (provider: 'google' | 'microsoft') => {
    const stateObj = { provider, portalType: 'teacher' };
    const encodedState = btoa(JSON.stringify(stateObj));
    const redirectUri = encodeURIComponent(`${window.location.origin}/portal/sso/callback`);
    
    if (provider === 'google') {
      window.location.href = `/api/auth/sso/authorize?provider=google&state=${encodedState}&redirectUri=${redirectUri}`;
    } else {
      window.location.href = `/api/auth/sso/authorize?provider=microsoft&state=${encodedState}&redirectUri=${redirectUri}`;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#0f172a' : '#faf5ff',
        background: 'radial-gradient(circle at 10% 20%, rgba(139, 92, 246, 0.08) 0%, transparent 40%), radial-gradient(circle at 90% 80%, rgba(99, 102, 241, 0.08) 0%, transparent 40%)'
      }}
    >
      {/* Top action: language & back to hub */}
      <Box sx={{ position: 'absolute', top: 20, right: 24, display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="text"
          startIcon={<ArrowBackIcon />}
          href="/Externel_apps/index.html"
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Hub Applications
        </Button>
        <IconButton size="small" onClick={(e) => setLangAnchor(e.currentTarget)}>
          <LanguageIcon />
        </IconButton>
        <Menu anchorEl={langAnchor} open={Boolean(langAnchor)} onClose={() => setLangAnchor(null)}>
          <MenuItem onClick={() => { setLanguage('fr'); setLangAnchor(null); }}>Français</MenuItem>
          <MenuItem onClick={() => { setLanguage('en'); setLangAnchor(null); }}>English</MenuItem>
          <MenuItem onClick={() => { setLanguage('ar'); setLangAnchor(null); }}>العربية</MenuItem>
        </Menu>
      </Box>

      <Paper
        elevation={4}
        sx={{
          maxWidth: 440,
          width: '100%',
          p: { xs: 3, sm: 4.5 },
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
          bgcolor: 'background.paper'
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              color: 'white',
              boxShadow: '0 8px 20px rgba(139, 92, 246, 0.35)',
              mb: 2
            }}
          >
            <SchoolIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">
            {t('portal.teacher.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('portal.teacher.subtitle')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              required
              fullWidth
              label={t('portal.teacher.login_label')}
              placeholder={t('portal.teacher.login_placeholder')}
              value={loginInput}
              onChange={(e) => setLoginInput(e.target.value)}
              disabled={loading}
              autoFocus
            />

            <TextField
              required
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label={t('portal.teacher.password_label')}
              placeholder={t('portal.teacher.password_placeholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              color="secondary"
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                  boxShadow: '0 6px 20px rgba(139, 92, 246, 0.4)'
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('portal.teacher.btn_login')}
            </Button>
          </Stack>
        </form>

        <Box sx={{ my: 3, display: 'flex', alignItems: 'center' }}>
          <Divider sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, fontWeight: 600 }}>
            {t('portal.teacher.or_sso')}
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>

        <Stack spacing={1.5}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => handleSsoClick('google')}
            startIcon={<GoogleSvgIcon />}
            sx={{
              py: 1.2,
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': { borderColor: '#4285F4', bgcolor: 'rgba(66, 133, 244, 0.04)' }
            }}
          >
            {t('portal.teacher.sso_google')}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => handleSsoClick('microsoft')}
            startIcon={<MicrosoftSvgIcon />}
            sx={{
              py: 1.2,
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': { borderColor: '#00a4ef', bgcolor: 'rgba(0, 164, 239, 0.04)' }
            }}
          >
            {t('portal.teacher.sso_microsoft')}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};
